/**
 * Sticky "acting as client org" banner — renders only while an owner support session is live.
 *
 * OrgDetail (admin console) starts impersonation by stashing the REAL admin session under
 * localStorage "th_auth_orig" and the session metadata under "th_imp" before swapping in the
 * scoped token. This banner shows the countdown and restores the original session on demand.
 * Mount it inside UsersApp, just above <Outlet />.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { setSession, type Session } from "../lib/auth.js";
import { Alert } from "./icons.js";

const ORIG_KEY = "th_auth_orig";
const IMP_KEY = "th_imp";

interface ImpInfo {
  orgId: string;
  orgName: string;
  expiresAt: string;
}

function readImp(): ImpInfo | null {
  try {
    const raw = localStorage.getItem(IMP_KEY);
    const orig = localStorage.getItem(ORIG_KEY);
    if (!raw || !orig) return null;
    const parsed = JSON.parse(raw) as Partial<ImpInfo>;
    if (typeof parsed.orgName !== "string" || typeof parsed.expiresAt !== "string") return null;
    return {
      orgId: typeof parsed.orgId === "string" ? parsed.orgId : "",
      orgName: parsed.orgName,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

export function ImpersonationBanner(): ReactNode {
  const [imp] = useState<ImpInfo | null>(() => readImp());
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!imp) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [imp]);

  if (!imp) return null;

  const secs = Math.max(0, Math.floor((new Date(imp.expiresAt).getTime() - now) / 1000));
  const clock = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;

  const endSession = (): void => {
    try {
      const raw = localStorage.getItem(ORIG_KEY);
      if (raw) setSession(JSON.parse(raw) as Session);
    } catch {
      /* restoring failed — the scoped token expires on its own */
    }
    localStorage.removeItem(ORIG_KEY);
    localStorage.removeItem(IMP_KEY);
    window.location.assign("/admin");
  };

  return (
    <div className="t1s-imp" role="status">
      <Alert />
      <span className="t1s-imp-text">
        You are acting as <b>{imp.orgName}</b> — support session, scoped and time-boxed ·{" "}
        <span className="tnum t1s-imp-clock">{secs === 0 ? "expired" : `${clock} left`}</span> · every
        action is logged
      </span>
      <button type="button" className="btn sm" onClick={endSession}>
        Return to admin
      </button>
    </div>
  );
}
